import { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView, Image, ActivityIndicator, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { AttachmentFile } from "@/components/Attachment/types";
import { getFileIcon, getFileColor, formatFileSize } from "@/components/Attachment/lib/fileUtils";
import { supabase } from "@/lib/supabase-client";
import { FileItem } from "../components/FileItem";
import { FileViewProps } from "../types";

function FilePreviewPanel({
  file,
  onExpandImage,
}: {
  file: AttachmentFile | null;
  onExpandImage?: (url: string, name: string) => void;
}) {
  const { colors } = useTheme();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isImage = file?.category === "image" || file?.type?.startsWith("image/");
  const isText = file?.type === "text/plain" || file?.name.endsWith(".txt");
  const isPdf = file?.type === "application/pdf" || file?.name.endsWith(".pdf");

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      setTextContent(null);
      return;
    }

    setLoading(true);
    setPreviewUrl(null);
    setTextContent(null);

    const loadPreview = async () => {
      try {
        if (file.bucket && file.uri) {
          const { data } = await supabase.storage.from(file.bucket).createSignedUrl(file.uri, 3600);
          if (data?.signedUrl) {
            if (isImage || isPdf) {
              setPreviewUrl(data.signedUrl);
            } else if (isText) {
              const response = await fetch(data.signedUrl);
              const text = await response.text();
              setTextContent(text);
            }
          }
        } else if (file.uri && !file.bucket) {
          if (isImage || isPdf) {
            setPreviewUrl(file.uri);
          } else if (isText) {
            const response = await fetch(file.uri);
            const text = await response.text();
            setTextContent(text);
          }
        }
      } catch (err) {
        console.error("Failed to load preview:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id, file?.uri, file?.bucket, isImage, isText, isPdf]);

  // No file selected
  if (!file) {
    return (
      <View style={[styles.previewPanel, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
        <MaterialCommunityIcons name="file-outline" size={48} color={colors.textSecondary} />
        <ThemedText style={[styles.previewMessage, { color: colors.textSecondary }]}>
          Select a file to preview
        </ThemedText>
      </View>
    );
  }

  const icon = getFileIcon(file);
  const iconColor = getFileColor(file);

  // Loading state
  if (loading) {
    return (
      <View style={[styles.previewPanel, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Image preview
  if (isImage && previewUrl) {
    return (
      <Pressable
        style={[styles.previewPanel, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
        onPress={() => onExpandImage?.(previewUrl, file.name)}
      >
        <Image source={{ uri: previewUrl }} style={styles.previewImage} resizeMode="contain" />
        <View style={styles.previewFooter}>
          <ThemedText style={[styles.previewMeta, { color: colors.textSecondary }]}>
            {formatFileSize(file.size)}
          </ThemedText>
          <ThemedText style={[styles.expandHint, { color: colors.primary }]}>Click to expand</ThemedText>
        </View>
      </Pressable>
    );
  }

  // PDF preview
  if (isPdf && previewUrl) {
    return (
      <View
        style={[
          styles.previewPanel,
          styles.pdfPreviewPanel,
          { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
        ]}
      >
        <View style={styles.pdfContainer}>
          <iframe src={previewUrl} style={{ width: "100%", height: "100%", border: "none", borderRadius: 4 }} />
        </View>
        <ThemedText style={[styles.previewMeta, { color: colors.textSecondary }]}>
          {formatFileSize(file.size)}
        </ThemedText>
      </View>
    );
  }

  // Text preview
  if (isText && textContent !== null) {
    return (
      <View style={[styles.previewPanel, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
        <ScrollView style={styles.textPreviewScroll} contentContainerStyle={styles.textPreviewContent}>
          <ThemedText style={[styles.textPreview, { color: colors.text }]}>{textContent}</ThemedText>
        </ScrollView>
        <ThemedText style={[styles.previewMeta, { color: colors.textSecondary }]}>
          {formatFileSize(file.size)}
        </ThemedText>
      </View>
    );
  }

  // No preview available
  return (
    <View style={[styles.previewPanel, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
      <MaterialCommunityIcons name={icon as any} size={64} color={iconColor} />
      <ThemedText style={[styles.previewMeta, { color: colors.textSecondary, marginTop: 12 }]}>
        {formatFileSize(file.size)}
      </ThemedText>
      <ThemedText style={[styles.noPreviewText, { color: colors.textSecondary }]}>No preview for this type</ThemedText>
    </View>
  );
}

export function ColumnsView({ files, onFileRemove, onFileRename, onExpandImage }: FileViewProps) {
  const { colors } = useTheme();
  const [selectedFile, setSelectedFile] = useState<AttachmentFile | null>(null);

  // Auto-select first file if none selected
  useEffect(() => {
    if (files.length > 0 && !selectedFile) {
      setSelectedFile(files[0]);
    } else if (files.length === 0) {
      setSelectedFile(null);
    }
  }, [files, selectedFile]);

  // Clear selection if selected file was removed
  useEffect(() => {
    if (selectedFile && !files.find((f) => f.id === selectedFile.id)) {
      setSelectedFile(files.length > 0 ? files[0] : null);
    }
  }, [files, selectedFile]);

  return (
    <View style={styles.container}>
      {/* File List */}
      <ScrollView style={[styles.fileList, { borderColor: colors.border }]}>
        {files.map((file) => (
          <FileItem
            key={file.id}
            file={file}
            mode="columns"
            onRemove={onFileRemove ? () => onFileRemove(file.id) : undefined}
            onRename={onFileRename ? (newName) => onFileRename(file.id, newName) : undefined}
            onPreview={() => setSelectedFile(file)}
            selected={selectedFile?.id === file.id}
          />
        ))}
      </ScrollView>

      {/* Preview Panel */}
      <FilePreviewPanel file={selectedFile} onExpandImage={onExpandImage} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    gap: 12,
  },
  fileList: {
    flex: 1,
    maxWidth: 280,
    borderRightWidth: StyleSheet.hairlineWidth,
  },
  previewPanel: {
    flex: 2,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  pdfPreviewPanel: {
    padding: 8,
  },
  previewImage: {
    flex: 1,
    width: "100%",
    maxHeight: 300,
  },
  pdfContainer: {
    flex: 1,
    width: "100%",
    minHeight: 400,
    borderRadius: 4,
    overflow: "hidden",
  },
  previewInfo: {
    marginTop: 12,
    alignItems: "center",
  },
  previewFilename: {
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
  },
  previewFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginTop: 4,
  },
  previewMeta: {
    fontSize: 12,
  },
  expandHint: {
    fontSize: 12,
    fontWeight: "500",
  },
  previewMessage: {
    marginTop: 8,
    fontSize: 14,
  },
  noPreviewText: {
    marginTop: 16,
    fontSize: 13,
  },
  textPreviewScroll: {
    flex: 1,
    width: "100%",
  },
  textPreviewContent: {
    padding: 8,
  },
  textPreview: {
    fontSize: 12,
    fontFamily: "monospace",
    lineHeight: 18,
  },
});
